/**
 * The cross-mode moderation display: what the composer shows an author once a
 * moderation decision has been made about their post.
 *
 * Every moderation mode reports a decision the same way, so nothing here
 * depends on which mode the space runs — it renders off the comment's own
 * moderation state and off the rejection the last save came back with. The
 * mode-specific banners live beside this in `mode-banners.tsx`.
 *
 * The decisions themselves are made server-side in
 * `server/modules/comments.ts`; this file owns the report banner, the appeal
 * flow that calls `comments.confirm`, and the admin-bypass heads-up.
 */

import { Send } from '@carbon/icons-react';
import type { Comment } from '@repo/shared/orpc/procedures/comments';
import { useTheme } from '@repo/theme';
import { AITag } from 'component/aitag';
import { AiHelperBanner } from 'component/banner/ai-helper-banner';
import { ModerationBanner, type ModerationViolation } from 'component/banner/moderation-banner';
import { Pad } from 'component/basics';
import { SubtleButton } from 'component/button';
import type { CommentRejection, DraftComment } from 'component/comment';
import { ModerationAdminBypassModal } from 'component/modal/moderation/admin-bypass';
import { CommunityGuidelinesModal } from 'component/modal/moderation/community-guidelines';
import { SendToModeratorModal } from 'component/modal/moderation/send-to-moderator-modal';
import { ModerationThankYouModal } from 'component/modal/moderation/thank-you';
import { RichText } from 'component/richtext';
import { Heading } from 'component/text';
import { confirmCommentAsync } from 'data/comment';
import { useSettings } from 'data/settings';
import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { useIsAdmin } from 'util/admin';
import { trackEvent } from 'util/analytics';
import { getRuleLabelByKey } from 'util/community-guidelines';
import { useDatastore, useTrackEvent } from 'util/datastore';
import { fireAndForget } from 'util/hooks';
import { t } from 'util/stdtypes';

type SystemDownBannerProps = {
    comment: Comment;
    onEditingDone?: () => void;
    showModerationModal: boolean;
    setShowModerationModal: (show: boolean) => void;
};

export function SystemDownBanner({ comment, onEditingDone, showModerationModal, setShowModerationModal }: SystemDownBannerProps) {
    const { colors } = useTheme();
    return (
        <View
            style={{ backgroundColor: colors.colorSurfaceBannerWarning, paddingHorizontal: 20, paddingVertical: 16 }}
            testID='system-down-banner'
        >
            <AITag />
            <Pad size={16} />
            <Heading
                color={colors.colorTextSecondaryOnBanner}
                label={t('moderation.system_unavailable_title', '⚠️ Moderation system temporarily unavailable')}
            />
            <Pad size={16} />
            <RichText
                color={colors.colorTextSecondaryOnBanner}
                label={t(
                    'moderation.system_unavailable_message',
                    'Our AI moderation system is temporarily unavailable. You can still send your post to a human moderator for review.',
                )}
            />
            <Pad size={20} />
            <SubtleButton
                color={colors.colorTextSecondaryOnBanner}
                icon={Send}
                label={t('moderation.send_for_review_button', 'Send to a moderator for review')}
                onPress={() => setShowModerationModal(true)}
            />
            {showModerationModal && (
                <ModerationModal
                    commentKey={comment.commentKey}
                    onClose={() => setShowModerationModal(false)}
                    onEditingDone={onEditingDone}
                />
            )}
        </View>
    );
}

export type ModerationReportProps = {
    comment: DraftComment;
    /**
     * Report the perspective's violations rather than the body's. Reworded
     * accordingly, and without the confirm and appeal paths — a rejected
     * perspective has no moderator review to appeal to.
     */
    isPerspectiveModeration?: boolean;
    onEditingDone?: () => void;
    /**
     * Why the last save was refused. `kind` says which flow refused it and so
     * how this renders: a refused comment body can be confirmed or appealed,
     * while a refused perspective can only be edited, so that variant drops
     * the bypass and send-to-moderator paths.
     */
    rejection?: CommentRejection | null;
};

export function ModerationReport({ comment, isPerspectiveModeration = false, onEditingDone, rejection }: ModerationReportProps) {
    const showExtraModerationMessage = useSettings()?.showExtraModerationMessage;
    const [showModerationModal, setShowModerationModal] = useState(false);
    const [showCommunityGuidelines, setShowCommunityGuidelines] = useState(false);
    const viewerIsAdmin = useIsAdmin();

    // A rejected perspective is reported beside the field it came from, and it
    // takes precedence: the author has to change it, so telling them about the
    // body as well would offer a way past a rejection they cannot post past.
    if (!isPerspectiveModeration && rejection?.perspectiveViolations?.length) return null;

    // In the composer, an admin's rejected post is auto-confirmed (admins post
    // anyway at any severity) and the admin is shown a heads-up that it was
    // flagged but bypassed.
    if (rejection && viewerIsAdmin && !isPerspectiveModeration) {
        return <AdminBypassFlow onEditingDone={onEditingDone} rejection={rejection} />;
    }

    // A minor violation is coaching rather than a block: the author gets AI
    // writing guidance naming the guidelines they tripped, not the warning
    // banner's appeal paths. A rejected perspective is never minor.
    if (rejection?.minorViolation && !isPerspectiveModeration) {
        return (
            <View testID='moderation-report'>
                <AiHelperBanner tips={rejection.violations.map(getRuleLabelByKey)} />
            </View>
        );
    }

    const violation: ModerationViolation | null = rejection
        ? { violations: rejection.violations, moderationSystemDown: rejection.moderationSystemDown }
        : comment.moderationState
          ? {
                violations: comment.moderationState.violations,
                pending: comment.moderationState.inReview,
                time: comment.createdAt,
            }
          : null;
    if (!violation) return null;

    const effectiveCommentKey = rejection?.commentKey ?? comment.commentKey;

    return (
        <>
            <ModerationBanner
                isPerspectiveModeration={isPerspectiveModeration}
                onSendToModerator={() => setShowModerationModal(true)}
                onShowCommunityGuidelines={() => setShowCommunityGuidelines(true)}
                showExtraModerationMessage={!!showExtraModerationMessage}
                violation={violation}
            />
            {showModerationModal && effectiveCommentKey && (
                <ModerationModal
                    commentKey={effectiveCommentKey}
                    onClose={() => setShowModerationModal(false)}
                    onEditingDone={onEditingDone}
                />
            )}
            {showCommunityGuidelines && <CommunityGuidelinesModal onClose={() => setShowCommunityGuidelines(false)} />}
        </>
    );
}

type ModerationModalProps = {
    commentKey: string;
    onClose: () => void;
    onEditingDone?: () => void;
};

function ModerationModal({ commentKey, onClose, onEditingDone }: ModerationModalProps) {
    const [sent, setSent] = useState(false);
    const datastore = useDatastore();
    useTrackEvent('moderation-modal', { commentKey });

    async function onSubmit(appealText: string) {
        await confirmCommentAsync(datastore, { commentKey, appealText });
        setSent(true);
        trackEvent(datastore, 'moderation-appeal', { text: appealText, commentKey });
    }

    function onDone() {
        onClose();
        if (onEditingDone) {
            onEditingDone();
        }
    }

    if (sent) {
        return <ModerationThankYouModal onClose={onDone} />;
    }

    return <SendToModeratorModal onClose={onClose} onSubmit={onSubmit} />;
}

type AdminBypassFlowProps = {
    rejection: CommentRejection;
    onEditingDone?: () => void;
};

/**
 * Auto-confirms an admin's rejected post (the server posts it live and records
 * the bypass for moderators) and shows the heads-up warning — a
 * `ModerationReport` of what would have been blocked. Closing it finishes the
 * composer.
 */
function AdminBypassFlow({ rejection, onEditingDone }: AdminBypassFlowProps) {
    const datastore = useDatastore();
    const showExtraModerationMessage = useSettings()?.showExtraModerationMessage;
    const confirmedRef = useRef(false);
    useEffect(() => {
        if (confirmedRef.current) return;
        confirmedRef.current = true;
        fireAndForget(confirmCommentAsync(datastore, { commentKey: rejection.commentKey }));
    }, [datastore, rejection.commentKey]);

    const violation: ModerationViolation = { violations: rejection.violations };

    return (
        <ModerationAdminBypassModal onClose={() => onEditingDone?.()}>
            <ModerationBanner isAdminBypassing showExtraModerationMessage={!!showExtraModerationMessage} violation={violation} />
        </ModerationAdminBypassModal>
    );
}
